<script lang="ts">
  import { getContext, onMount } from 'svelte';
  import { ResponsiveVars } from '@twist/core';
  import { cx } from '@emotion/css';

  import type { Carousel } from '$lib/sanity/schemas';
  import { key, type SanityContext } from '$lib/context/sanity';
  import CarouselWrapper from './CarouselWrapper.svelte';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: Carousel;
    sourceMap: ContentSourceMap | undefined;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);

  let carouselElement: HTMLDivElement | undefined = $state();
  let carouselWidth: number | undefined = $state();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  onMount(() => {
    if (carouselElement) {
      carouselElement.scrollLeft = 0;
    }
  });

  const responsiveVars = new ResponsiveVars()
    .addFluid('carousel-gap', [
      ['min', 24],
      ['lg', 'max', 24, 32],
      ['max', 32]
    ])
    .addFluid('fullwidth-spacer', [
      ['min', 24],
      ['lg', 'max', 68, 136],
      ['max', 68]
    ])
    .addFluid('caption-margin-top', [
      ['min', 24],
      ['lg', 'max', 24, 32],
      ['max', 32]
    ])
    .toCSS();
</script>

<CarouselWrapper fullWidth={document.fullWidth}>
  {#snippet carousel()}
    <div
      class={cx(responsiveVars, 'carousel')}
      class:fullWidth={document.fullWidth}
      bind:this={carouselElement}
      bind:clientWidth={carouselWidth}
      style="--carousel-width: {carouselWidth}px"
    >
      {#each document.images as image, index}
        {#if image}
          <div
            class="image-container"
            data-sanity={encodeDataAttribute([...path, 'imageRefs', index])}
          >
            <img
              src={urlBuilder.image(image.asset).auto('format').url()}
              alt={image.asset.altText}
            />
          </div>
        {/if}
      {/each}
    </div>
  {/snippet}

  {#snippet caption()}
    <div data-sanity={encodeDataAttribute([...path, 'caption'])}>
      {#if document.caption}
        <p class="caption">
          {document.caption}
        </p>
      {/if}
    </div>
  {/snippet}
</CarouselWrapper>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  $caption-width: 300px;

  .carousel {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: var(--carousel-gap);
    padding-bottom: var(--carousel-gap);
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
  }

  .carousel::-webkit-scrollbar {
    height: 5px;
  }

  .carousel::-webkit-scrollbar-thumb {
    background-color: var(--theme-general-foreground-color, $general-foreground-color);
  }

  .carousel::-webkit-scrollbar-track {
    background-color: var(--theme-general-foreground-color, $general-foreground-color);
    background-clip: padding-box;
    border-bottom: 2px solid transparent;
    border-top: 2px solid transparent;
    margin: 0;

    @include at('lg') {
      margin-right: calc(var(--carousel-width) - $caption-width);
    }
  }

  // Padding and margins based on .container padding styles
  .fullWidth {
    &.carousel {
      padding-inline: var(--fullwidth-spacer);
    }

    &.carousel::-webkit-scrollbar-track {
      margin-left: var(--fullwidth-spacer);
      margin-right: var(--fullwidth-spacer);

      @include at('lg') {
        margin-right: calc(var(--carousel-width) - $caption-width - var(--fullwidth-spacer));
      }
    }
  }

  .image-container {
    flex: 0 0 auto;
    max-width: 75%;
    scroll-snap-align: center;

    img {
      border-radius: var(--theme-general-image-border-radius, 20px);
      object-fit: cover;
    }
  }

  .caption {
    font-family: var(--theme-text-mobile-caption-font-family, var(--font-family-secondary));
    font-size: var(--theme-text-mobile-caption-font-size, 14px);
    font-weight: var(--theme-text-mobile-caption-font-weight, 400);
    line-height: var(--theme-text-mobile-caption-line-height, 140%);
    color: var(--theme-general-foreground-color, $text-default-color);
    margin-top: var(--caption-margin-top);

    @include at('lg') {
      max-width: $caption-width;
    }
  }
</style>
